import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import {
  fetchMatches,
  fetchActivityFeed,
  fetchMatch,
  createMatch,
  updateMatch,
  deleteMatch,
  addMatchPlayer,
  updateMatchPlayer,
  removeMatchPlayer,
  createMatchTeam,
  updateMatchTeam,
  deleteMatchTeam,
  addMatchTeamPlayer,
  removeMatchTeamPlayer,
  createSession,
  updateSession,
  activateSession,
  deactivateSession,
  deleteSession,
  reorderSessions,
} from '../api/matches'

export function useMatches() {
  return useQuery({
    queryKey: ['matches'],
    queryFn: async () => {
      const data = await fetchMatches()
      cacheQueryData('matches', data)
      return data
    },
    initialData: () => getCachedQueryData('matches'),
    initialDataUpdatedAt: 0,
  })
}

export function useActivityFeed(followedPlayerIds = new Set(), enabled = true) {
  const sorted = [...followedPlayerIds].sort()
  const cacheKey = `activity-feed_${sorted.join(',')}`
  return useQuery({
    queryKey: ['activity-feed', sorted],
    queryFn: async () => {
      const data = await fetchActivityFeed(followedPlayerIds)
      cacheQueryData(cacheKey, data)
      return data
    },
    staleTime: 60_000,
    enabled,
    initialData: () => getCachedQueryData(cacheKey),
    initialDataUpdatedAt: 0,
  })
}

export function useMatch(id) {
  return useQuery({
    queryKey: ['match', id],
    queryFn: async () => {
      const data = await fetchMatch(id)
      cacheQueryData(`match_${id}`, data)
      return data
    },
    enabled: !!id,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    initialData: () => getCachedQueryData(`match_${id}`),
    initialDataUpdatedAt: 0,
  })
}

export function useCreateMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createMatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] })
      queryClient.invalidateQueries({ queryKey: ['players'] })
    },
  })
}

export function useUpdateMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...updates }) => updateMatch(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['matches'] })
      queryClient.invalidateQueries({ queryKey: ['match', variables.id] })
    },
  })
}

export function useDeleteMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteMatch,
    onMutate: (id) => {
      const previous = queryClient.getQueryData(['matches'])
      queryClient.setQueryData(['matches'], (old) =>
        old?.filter((m) => m.id !== id)
      )
      return { previous }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['matches'], context.previous)
      }
    },
  })
}

export function useAddMatchPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addMatchPlayer,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

export function useUpdateMatchPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, match_id, ...updates }) => updateMatchPlayer(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

export function useRemoveMatchPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }) => removeMatchPlayer(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

// ── Match Teams ──

export function useCreateMatchTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createMatchTeam,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

export function useUpdateMatchTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, match_id, ...updates }) => updateMatchTeam(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

export function useDeleteMatchTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, match_id }) => deleteMatchTeam(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

export function useAddMatchTeamPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addMatchTeamPlayer,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

export function useRemoveMatchTeamPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, match_id }) => removeMatchTeamPlayer(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

// ── Sessions ──

export function useCreateSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createSession,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

export function useUpdateSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateSession,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}

export function useActivateSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: activateSession,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['game'] })
      queryClient.invalidateQueries({ queryKey: ['matchGames', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['sideBets', variables.match_id] })
    },
  })
}

export function useDeactivateSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deactivateSession,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['game'] })
      queryClient.invalidateQueries({ queryKey: ['matchGames', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

export function useDeleteSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, match_id }) => deleteSession(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['game'] })
      queryClient.invalidateQueries({ queryKey: ['matchGames', variables.match_id] })
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

export function useReorderSessions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ updates, match_id }) => reorderSessions(updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.match_id] })
    },
  })
}
